import { type CitAdjustmentRow as CitAdjustmentRowData } from '../../../hooks/useCitAdjustments'
import { useCurrency } from '../../../hooks/useCurrency'
import { CitAdjustmentRow } from './CitAdjustmentRow'

interface Props {
  rows: CitAdjustmentRowData[]
  ytdProfitBeforeTax: number
  totalDisallowables: number
  totalAllowables: number
  assessableProfit: number
  disabled?: boolean
}

export function CitAdjustmentsSection({
  rows,
  ytdProfitBeforeTax,
  totalDisallowables,
  totalAllowables,
  assessableProfit,
  disabled,
}: Props) {
  const { format } = useCurrency()

  const disallowables = rows
    .filter(r => r.direction === 'disallowable')
    .sort((a, b) => a.displayOrder - b.displayOrder)
  const allowables = rows
    .filter(r => r.direction === 'allowable')
    .sort((a, b) => a.displayOrder - b.displayOrder)

  return (
    <div className="space-y-4">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        Adjustments
      </p>

      {/* Add back: Disallowable Expenses */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-1">
        <p className="text-xs font-semibold text-slate-700 mb-2">
          Add back: Disallowable Expenses
        </p>
        {disallowables.map(r => (
          <CitAdjustmentRow key={r.id} row={r} disabled={disabled} />
        ))}
        <div className="border-t border-slate-200 mt-2 pt-2 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-900">Total Disallowables</p>
          <p className="tabular-nums whitespace-nowrap text-sm font-semibold text-slate-900">
            {format(totalDisallowables)}
          </p>
        </div>
      </div>

      {/* Less: Allowable Deductions */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-1">
        <p className="text-xs font-semibold text-slate-700 mb-2">
          Less: Allowable Deductions
        </p>
        {allowables.map(r => (
          <CitAdjustmentRow key={r.id} row={r} disabled={disabled} />
        ))}
        <div className="border-t border-slate-200 mt-2 pt-2 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-900">Total Allowables</p>
          <p className="tabular-nums whitespace-nowrap text-sm font-semibold text-slate-900">
            {format(totalAllowables)}
          </p>
        </div>
      </div>

      {/* Assessable profit */}
      <div className="border-t-2 border-slate-200 pt-3 space-y-1">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-600">Profit Before Tax (YTD)</p>
          <p className="tabular-nums whitespace-nowrap text-sm text-slate-700">
            {format(ytdProfitBeforeTax)}
          </p>
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-600">+ Disallowables</p>
          <p className="tabular-nums whitespace-nowrap text-sm text-slate-700">
            {format(totalDisallowables)}
          </p>
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-600">− Allowables</p>
          <p className="tabular-nums whitespace-nowrap text-sm text-slate-700">
            {format(totalAllowables)}
          </p>
        </div>
        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-sm font-semibold text-slate-900">Assessable Profit</p>
          <p className="tabular-nums whitespace-nowrap text-base font-bold text-brand-700">
            {format(assessableProfit)}
          </p>
        </div>
      </div>
    </div>
  )
}
